import { canAccessViewQuery, getLogoutUrl } from '../../api/users-api';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import posthog from 'posthog-js';
import { useUserSettings } from '../../utils/use-user-settings';
import { pagesConfig } from '../../shared/pages-config';
import { userPortalPagesConfig } from '../../shared/user-portal-pages-config';
import { Link, useNavigate } from 'react-router-dom';
import { auth } from '../../shared/firebase/firebase-config';
import { useStore } from '../state/zustand-store';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import ListItem from '@mui/material/ListItem';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Email from '@mui/icons-material/Email';
import Help from '@mui/icons-material/Help';
import Logout from '@mui/icons-material/Logout';
import Paid from '@mui/icons-material/Paid';
import Settings from '@mui/icons-material/Settings';
import Support from '@mui/icons-material/Support';
interface UserPopoutProps {
  open: boolean;
  handleClose: () => void;
  anchorEl: HTMLElement | null;
  isUserPortal: boolean;
}

export default function UserPopout({
  open,
  handleClose,
  anchorEl,
  isUserPortal,
}: UserPopoutProps) {
  const {
    timeout,
    emailDomain,
    loggedInUser,
    userName,
    currentTier,
    roleName,
  } = useUserSettings();
  const navigate = useNavigate();
  const { setNotifications } = useStore();

  async function handleLogout() {
    await getLogoutUrl();
    await auth.signOut();
    posthog.reset();
    setNotifications([]);
    navigate('/login');
  }

  const { data: canView } = useQuery(canAccessViewQuery(emailDomain));

  if (isUserPortal) {
    return (
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: -60,
          horizontal: 'right',
        }}
        open={open}
        onClose={handleClose}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '4px',
          },
        }}
      >
        <ListItem sx={{ p: 0, px: 2 }}>
          <ListItemText primary={userName} secondary={loggedInUser} />
        </ListItem>
        <Divider sx={{ my: 1 }} />

        {canView && (
          <>
            <MenuItem
              component={Link}
              to={`./${pagesConfig.viewAdmin.link}`}
              rel="noopener noreferrer"
              onClick={handleClose}
            >
              <ListItemIcon>
                <Settings fontSize="small" />
              </ListItemIcon>
              gPanel View Settings
            </MenuItem>
            <Divider />
          </>
        )}

        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    );
  }

  return (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: -60,
        horizontal: 'right',
      }}
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiPaper-root': {
          borderRadius: '4px',
        },
      }}
    >
      <ListItem sx={{ p: 0, px: 2 }}>
        <ListItemText primary={userName} secondary={loggedInUser} />
      </ListItem>

      <ListItem sx={{ display: 'flex', gap: 1 }}>
        <Chip avatar={<AccountCircle />} size="small" label={roleName} />
        <Chip
          avatar={<Paid />}
          size="small"
          label={
            <span style={{ textTransform: 'capitalize' }}>{currentTier}</span>
          }
        />
      </ListItem>

      <Divider sx={{ my: 1 }} />
      {currentTier === 'trial' && (
        <>
          <ListItem>
            <Alert severity="warning" variant="filled" sx={{ py: 0 }}>
              Trial ends {dayjs(timeout).format('MM/DD/YYYY')}
            </Alert>
          </ListItem>
          <MenuItem
            component="a"
            href="https://promevo.com/contact-sales-google-workspace?utm_source=gpanel&utm_content=transferbutton"
            target="_blank"
            rel="noopener noreferrer"
          >
            <ListItemIcon>
              <Email fontSize="small" />
            </ListItemIcon>
            Contact Sales
          </MenuItem>
        </>
      )}
      {canView && (
        <MenuItem
          component={Link}
          to={`/${pagesConfig.viewAdmin.link}`}
          rel="noopener noreferrer"
          onClick={handleClose}
        >
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          gPanel View Settings
        </MenuItem>
      )}
      <MenuItem
        component={Link}
        to={`/${userPortalPagesConfig.userPortal.link}`}
        rel="noopener noreferrer"
        target="_blank"
      >
        <ListItemIcon>
          <AccountCircle fontSize="small" />
        </ListItemIcon>
        User Portal
      </MenuItem>

      <MenuItem
        component="a"
        href="https://gpanel.io/knowledge"
        target="_blank"
        rel="noopener noreferrer"
      >
        <ListItemIcon>
          <Help fontSize="small" />
        </ListItemIcon>
        Documentation
      </MenuItem>

      <MenuItem
        component="a"
        href="https://gpanel.io/support"
        target="_blank"
        rel="noopener noreferrer"
      >
        <ListItemIcon>
          <Support fontSize="small" />
        </ListItemIcon>
        Contact Support
      </MenuItem>

      <Divider />

      <MenuItem onClick={handleLogout}>
        <ListItemIcon>
          <Logout fontSize="small" />
        </ListItemIcon>
        Logout
      </MenuItem>
    </Menu>
  );
}
